import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiChatInputRequestStatus } from '@amitable/shared/ai-chat-decisions'
import {
  isUnanswered,
  useAiChatDecisionSteps,
} from './use-ai-chat-decision-steps'
import type {
  AiChatDecisionAnswer,
  AiChatInputRequestData,
} from '@amitable/shared/ai-chat-decisions'

export type AiChatDecisionCardProps = {
  request: AiChatInputRequestData
  disabled?: boolean
  onAnswer?: (
    request: AiChatInputRequestData,
    answers: Array<AiChatDecisionAnswer>,
  ) => Promise<void>
  onCancel?: (request: AiChatInputRequestData) => Promise<void>
}

function ownDraftAnswer(
  draft: Record<string, AiChatDecisionAnswer>,
  questionId: string,
) {
  return Object.hasOwn(draft, questionId) ? draft[questionId] : undefined
}

function resolveAnswer(
  request: AiChatInputRequestData,
  draft: Record<string, AiChatDecisionAnswer>,
  questionId: string,
) {
  const saved =
    request.status === AiChatInputRequestStatus.PENDING
      ? (ownDraftAnswer(draft, questionId) ??
        request.answers.find((answer) => answer.questionId === questionId))
      : request.answers.find((answer) => answer.questionId === questionId)

  return saved ?? { questionId, selectedOptionIds: [], customText: null }
}

export function useAiChatDecisionForm({
  request,
  disabled,
  onAnswer,
  onCancel,
}: AiChatDecisionCardProps) {
  const { t } = useTranslation('aiChat')
  const [draft, setDraft] = useState<Record<string, AiChatDecisionAnswer>>({})
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState<string>()
  const pending = request.status === AiChatInputRequestStatus.PENDING
  const locked = !pending || disabled || busy
  const answers = request.questions.map((question) =>
    resolveAnswer(request, draft, question.id),
  )
  const steps = useAiChatDecisionSteps({ answers, locked, onError: setError })
  const changeAnswer = (answer: AiChatDecisionAnswer) => {
    setError(undefined)
    setDraft((previous) => ({ ...previous, [answer.questionId]: answer }))
  }
  const perform = async (cancel: boolean) => {
    const supported = cancel ? onCancel : onAnswer
    if (locked || busyRef.current || !supported) return
    if (!cancel && answers.some(isUnanswered)) {
      setError(t('decisionRequired'))

      return
    }
    busyRef.current = true
    setBusy(true)
    setError(undefined)
    try {
      await sendDecision(cancel, { request, onAnswer, onCancel }, answers)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('decisionFailed'))
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  return {
    pending,
    locked,
    busy,
    error,
    answers,
    changeAnswer,
    perform,
    steps,
  }
}

async function sendDecision(
  cancel: boolean,
  props: AiChatDecisionCardProps,
  answers: Array<AiChatDecisionAnswer>,
) {
  if (cancel) {
    await props.onCancel?.(props.request)

    return
  }
  await props.onAnswer?.(
    props.request,
    answers.map((answer) => ({
      ...answer,
      customText: answer.customText?.trim() || null,
    })),
  )
}
